import constant from '@/constant'
import { getRecentlyUsedSlides, updateRecentlyUsedSlides } from '@/utils/recentlyUsedSlide.utils'
import { getSeenNewSlideTypes, addSeenNewSlideType } from '@/utils/seenNewSlideType.utils'

// state
const _state = {
  isSlideTypeNoAudio: false,
  recentlyUsedSlideNames: [],
  seenNewSlideTypeNames: [],
}

// Spinner Wheel ships as a native type inside the Quiz section; Duck Race and
// Escape Room are plugin slides sourced from the live marketplace catalogue. All
// are grouped under the Gamification section. (AHAM-515)
const SPINNER_WHEEL_TYPE = 'spinnerWheel'
const DUCK_RACE_TYPE = 'marketplace/duck-race'
const ESCAPE_ROOM_TYPE = 'marketplace/escape-room'

// The marketplace-sourced games featured in Gamification, in display order
// (Duck Race first, then Escape Room next to it).
const GAMIFICATION_MARKETPLACE_TYPES = [DUCK_RACE_TYPE, ESCAPE_ROOM_TYPE]

// Marketplace slide types, decorated for the picker, or [] when the marketplace
// flag is off / the catalogue is empty. Resolved once so the Gamification pull
// and the Marketplace section stay consistent. (AHAM-515)
const resolveMarketplaceTypes = (rootGetters) => {
  if (!rootGetters || !rootGetters['featureToggles/canUseNewSlideMarketPlace']) return []
  const marketplaceSlideTypes = rootGetters['slideMarketplace/marketplaceSlideTypes']
  if (!marketplaceSlideTypes || marketplaceSlideTypes.length === 0) return []
  return marketplaceSlideTypes.map((slide) => ({ ...slide, source: 'fromMarket', plugin: true }))
}

// The marketplace games to feature in Gamification, pulled from the resolved
// catalogue in GAMIFICATION_MARKETPLACE_TYPES order and skipping any the
// catalogue doesn't carry. (AHAM-515)
const resolveGamificationMarketplaceTypes = (marketplaceTypes) => {
  const games = GAMIFICATION_MARKETPLACE_TYPES
    .map((type) => marketplaceTypes.find((slide) => slide.type === type))
  return games.filter(Boolean)
}

// The Gamification section (games grouped together), or null when no game is
// available. Spinner Wheel is MOVED here from Quiz; the marketplace games are
// pulled from the catalogue. Degrades to Spinner-Wheel-only. (AHAM-515)
const buildGamificationSection = (spinnerWheel, marketplaceGames) => {
  const slideTypes = [spinnerWheel, ...marketplaceGames].filter(Boolean)
  if (slideTypes.length === 0) return null
  return {
    title: 'Gamification',
    sectionLabel: 'Gamification',
    sectionIcon: 'puzzle',
    sectionKey: 'gamification',
    desc: 'Play games and let your participants compete for victory!',
    slideTypes,
  }
}

// Rebuild the static sections immutably (never mutate the shared
// `constant.allTypeSections`), inserting Gamification immediately after Quiz and
// removing Spinner Wheel from the Quiz section it was moved out of. (AHAM-515)
const buildSectionsWithGamification = (sections, marketplaceGames) => {
  const list = []
  sections.forEach((section) => {
    if (!section || section.sectionKey !== 'quiz') {
      list.push(section)
      return
    }
    const spinnerWheel = section.slideTypes.find((slide) => slide.type === SPINNER_WHEEL_TYPE) || null
    list.push({
      ...section,
      slideTypes: section.slideTypes.filter((slide) => slide.type !== SPINNER_WHEEL_TYPE),
    })
    const gamification = buildGamificationSection(spinnerWheel, marketplaceGames)
    if (gamification) list.push(gamification)
  })
  return list
}

// The full feature-flagged Marketplace section, or null when empty. The
// Gamification games are featured in their own section, so they are dropped here
// to avoid a same-modal duplicate. (AHAM-515)
const buildMarketplaceSection = (marketplaceTypes, marketplaceGames) => {
  if (marketplaceTypes.length === 0) return null
  const featuredTypes = new Set(marketplaceGames.map((slide) => slide.type))
  const slideTypes = featuredTypes.size
    ? marketplaceTypes.filter((slide) => !featuredTypes.has(slide.type))
    : marketplaceTypes
  if (slideTypes.length === 0) return null
  return {
    title: 'Marketplace',
    sectionLabel: 'Marketplace',
    sectionIcon: 'puzzle',
    sectionKey: 'marketplace',
    desc: 'Marketplace slide types',
    slideTypes,
  }
}

// getters
const _getters = {
  // Builds the New-slide section list (also feeds the right-panel slide-type
  // dropdown and, flattened, the Treatment marketplace modal): the static
  // `constant.allTypeSections` plus a dynamic 'Gamification' section rendered
  // immediately after Quiz, plus the feature-flagged 'Marketplace' section.
  //
  // Because Gamification MOVES (not copies) Spinner Wheel and the marketplace
  // games out of their default sections, the flattened list stays one-per-type,
  // so the Treatment modal's `visibleSlideTypes` sees exactly one of each, no
  // duplicate. (AHAM-515)
  getSlideTypeList: (state, getters, rootState, rootGetters) => {
    const marketplaceTypes = resolveMarketplaceTypes(rootGetters)
    const marketplaceGames = resolveGamificationMarketplaceTypes(marketplaceTypes)
    const list = buildSectionsWithGamification(constant.allTypeSections, marketplaceGames)
    const marketplaceSection = buildMarketplaceSection(marketplaceTypes, marketplaceGames)
    if (marketplaceSection) list.push(marketplaceSection)
    return list
  },
  recentlyUsedSlideNames: (state) => state.recentlyUsedSlideNames,
  seenNewSlideTypeNames: (state) => state.seenNewSlideTypeNames,
}

// mutations
const mutations = {
  SET_RECENTLY_USED_SLIDE_NAMES(state, names) {
    state.recentlyUsedSlideNames = names
  },
  SET_SEEN_NEW_SLIDE_TYPE_NAMES(state, names) {
    state.seenNewSlideTypeNames = names
  },
}

// actions
const actions = {
  fetchRecentlyUsedSlides({ commit }, userId) {
    const names = getRecentlyUsedSlides(userId)
    commit('SET_RECENTLY_USED_SLIDE_NAMES', names)
  },
  updateRecentlyUsedSlides({ commit }, { userId, slideName }) {
    const names = updateRecentlyUsedSlides(userId, slideName)
    commit('SET_RECENTLY_USED_SLIDE_NAMES', names)
  },
  fetchSeenNewSlideTypes({ commit }, userId) {
    const names = getSeenNewSlideTypes(userId)
    commit('SET_SEEN_NEW_SLIDE_TYPE_NAMES', names)
  },
  markSlideTypeAsSeen({ commit }, { userId, slideName }) {
    const names = addSeenNewSlideType(userId, slideName)
    commit('SET_SEEN_NEW_SLIDE_TYPE_NAMES', names)
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  mutations,
  actions,
}
